import type { SettingsResponse } from "../../shared/types";
import type { PillTone } from "../components/ui/Pill";
import { opensLabel } from "./AddressStep";
import { lookName } from "./looks";
import { shownHost } from "./parts";
import { trackParts } from "./trackParts";

/** What a row on the ready screen offers: a way on, the address to copy, or a quiet state. */
export type ReadyAct = { kind: "go"; label: string; to: string; primary: boolean } | { kind: "copy"; value: string } | { kind: "pill"; tone?: PillTone; text: string };

export interface ReadyItem {
  key: "look" | "email" | "address" | "tracking";
  /** Done rows show a tick; the rest show their own icon and say what is left. */
  done: boolean;
  title: string;
  sub?: string;
  act: ReadyAct;
}

/** The four rows of the ready screen, each reading the same state Settings reads. */
export function readySummary(data: SettingsResponse, businessName: string): ReadyItem[] {
  const s = data.setup;
  const sending = data.sending;
  const host = shownHost(data.domain);
  const parts = trackParts(data);
  const loaded = parts.filter((p) => p.state === "loaded");
  const waiting = parts.filter((p) => p.state === "waiting");
  const look: ReadyItem = s.look
    ? { key: "look", done: true, title: "Your look", sub: lookName(data.brand), act: { kind: "go", label: "Change", to: "/settings/brand", primary: false } }
    : { key: "look", done: false, title: "Your look isn’t chosen yet", sub: `Using ${lookName(data.brand)}`, act: { kind: "go", label: "Choose", to: "/setup/look", primary: true } };
  const email: ReadyItem = s.email
    ? { key: "email", done: true, title: "Email is on", sub: `${data.brand.from_name || businessName}, ${sending.from_email ?? ""}`, act: { kind: "pill", tone: "good", text: "Verified" } }
    : {
        key: "email",
        done: false,
        title: "Email isn’t finished",
        sub: sending.domain ? "Records still to add" : "No sending address yet",
        act: { kind: "go", label: "Finish it", to: sending.domain ? "/setup/email/records" : "/setup/email", primary: true },
      };
  const address: ReadyItem = s.address
    ? { key: "address", done: true, title: "Your web address", sub: `${host} opens ${opensLabel(data.domain)}`, act: { kind: "copy", value: host } }
    : { key: "address", done: false, title: "Your web address isn’t confirmed", sub: host, act: { kind: "go", label: "Confirm", to: "/setup/address", primary: true } };
  const tracking: ReadyItem = loaded.length
    ? { key: "tracking", done: true, title: "Tracking is on", sub: loaded.map((p) => p.name).join(", "), act: { kind: "pill", tone: "good", text: "Loaded" } }
    : waiting.length
      ? { key: "tracking", done: true, title: "Tracking added", sub: waiting.map((p) => p.name).join(", "), act: { kind: "pill", text: "Waiting for a visit" } }
      : { key: "tracking", done: false, title: "Tracking skipped", act: { kind: "go", label: "Add later", to: "/settings/scripts", primary: false } };
  return [look, email, address, tracking];
}
